import { useMemo } from "react";
import { RecordingType } from "../../types/general-types";

export type VideoEvent = {
	/**
	 * Time in seconds
	 */
	key: string;
	id?: number;
	time: number;
	name: string;
	type: string;
	details: Record<string, any>;
	flag_level: number;
	logged_time: string;
	resolved: boolean;
	comment?: string;
};
export type TimestampsArr = {
	/**
	 * Time in seconds
	 */
	start?: number;
	/**
	 * Time in seconds
	 */
	end?: number;
	vid: VideoEvent["details"][string];
}[];

export type SessionRecordingTimestamps = Record<RecordingType, Record<number, TimestampsArr>>;

export const TIMESTAMP_PLACEHOLDER = { vid: "placeholder" };

/**
 * Combine all elements of the array that have the same vid
 *
 * eg: [{vid: 1, start: 1}, {vid: 1, end: 2}] -> [{vid: 1, start: 1, end: 2}]
 */
const consolidateTimestampsArr = (timestamps: TimestampsArr): TimestampsArr => {
	const copiedTimestamps = timestamps.map((timestamp) => ({ ...timestamp }));
	const consolidatedTimestamps: TimestampsArr = [];
	const vidToTimestamp: Map<string, TimestampsArr[number]> = new Map();

	for (const currentTimestamp of copiedTimestamps) {
		const currentVid = String(currentTimestamp.vid);

		const existingTimestamp = vidToTimestamp.get(currentVid);
		if (existingTimestamp) {
			if (currentTimestamp.start && !existingTimestamp.start) {
				existingTimestamp.start = currentTimestamp.start;
			}
			if (currentTimestamp.end && !existingTimestamp.end) {
				existingTimestamp.end = currentTimestamp.end;
			}
		} else {
			vidToTimestamp.set(currentVid, currentTimestamp);
			consolidatedTimestamps.push(currentTimestamp);
		}
	}

	return consolidatedTimestamps;
};

// Does parsing of the timestamps on the front end.
// Could potentially combine this with the timestamp parsing we do on the node-server
export const useTimestamps = (events: VideoEvent[]): SessionRecordingTimestamps => {
	return useMemo(() => {
		const sessionTimestamps: SessionRecordingTimestamps = { webcam: {}, screencapture: {} };
		let assessmentStartTime: number | undefined;
		let hasMicrophoneEvents = false;
		if (events) {
			for (const event of events) {
				if (event.name === "start quiz") {
					assessmentStartTime = event.time;
				}

				const isStartRecording = event.name === "start recording";
				const isEndRecording = event.name === "end recording";
				if ((isStartRecording || isEndRecording) && event.details.success) {
					const sourceIndex: number = event.details.sourceIndex ?? 0;
					let recordingType: RecordingType;
					switch (event.details.media) {
						case "display":
							recordingType = "screencapture";
							break;
						case "microphone":
							hasMicrophoneEvents = true;
							recordingType = "webcam";
							break;
						case "webcam":
							if (hasMicrophoneEvents) {
								console.error("Both microphone and webcam events are found");
								continue;
							} else {
								recordingType = "webcam";
							}
							break;
						default:
							console.error("Invalid recording event media type", event.details.media);
							continue;
					}

					const sourceTimestamps = sessionTimestamps[recordingType][sourceIndex] ?? [{ ...TIMESTAMP_PLACEHOLDER }];
					sessionTimestamps[recordingType][sourceIndex] = sourceTimestamps;

					if (isStartRecording) {
						// This condition is erroneous
						if (
							sourceTimestamps.length > 1 &&
							sourceTimestamps[sourceTimestamps.length - 1]?.vid === event.details.video
						) {
							sourceTimestamps[sourceTimestamps.length - 1].start = event.time;
						} else {
							sourceTimestamps.push({ start: event.time, vid: event.details.video });
						}
					} else if (isEndRecording) {
						if (
							sourceTimestamps.length > 1 &&
							sourceTimestamps[sourceTimestamps.length - 1]?.vid === event.details.video
						) {
							sourceTimestamps[sourceTimestamps.length - 1].end = event.time;
						}
						// This condition is erroneous
						else {
							sourceTimestamps.push({ end: event.time, vid: event.details.video });
						}
					}
				}
			}
		}

		if (!assessmentStartTime) {
			return { webcam: {}, screencapture: {} };
		}

		return Object.fromEntries(
			Object.entries(sessionTimestamps).map(([type, sources]) => [
				type,
				Object.fromEntries(
					Object.entries(sources).map(([index, timestamps]) => [
						index,
						consolidateTimestampsArr(timestamps).filter(({ end }) => !end || end > assessmentStartTime!),
					]),
				),
			]),
		) as SessionRecordingTimestamps;
	}, [events]);
};

export default useTimestamps;
